'use client';
import type { SanityDocument } from 'next-sanity';
import { useEffect, useState } from 'react';
import { BlogPost } from './blog-post';

type SuggestedPostsProps = {
  currentPostId: string;
  tag?: string;
  initialPosts?: SanityDocument[];
};

export const SuggestedPosts = ({
  currentPostId,
  tag,
  initialPosts,
}: SuggestedPostsProps) => {
  const [posts, setPosts] = useState<SanityDocument[]>(initialPosts || []);
  const [isLoading, setIsLoading] = useState(!initialPosts);

  useEffect(() => {
    // If we have initial posts, don't fetch
    if (initialPosts) {
      return;
    }

    const fetchSuggestedPosts = async () => {
      try {
        const params = new URLSearchParams();
        if (tag) params.append('tag', tag);
        params.append('currentPostId', currentPostId);

        const response = await fetch(`/api/posts/suggested?${params}`);
        const { posts: suggestedPosts } = await response.json();
        setPosts(suggestedPosts);
      } catch (error) {
        console.error('Error fetching suggested posts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchSuggestedPosts();
  }, [currentPostId, tag, initialPosts]);

  if (isLoading) {
    return (
      <div className="pb-12 max-md:pb-[30px]">
        <h2 className="font-interDisplay text-[48px] leading-[56px] max-md:text-[40px] max-md:leading-[48px] font-semibold mb-10 ">
          Explore more
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {Array.from({ length: 2 }).map((_, index) => (
            <div
              key={`loading-placeholder-${currentPostId}-${tag || 'no-tag'}-${index}`}
              className="h-[300px] bg-[#F7F7F7] rounded-lg animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="pb-12 max-md:pb-[30px]">
      <h2 className="font-interDisplay text-[48px] leading-[56px] max-md:text-[40px] max-md:leading-[48px] font-semibold mb-10 ">
        Explore more
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {posts.map((post) => (
          <BlogPost key={post._id} post={post} />
        ))}
      </div>
    </div>
  );
};
